import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Loader2, CalendarPlus, X, User } from "lucide-react";

const STATUS_META = {
  booked:    { label: "Prenotato",  cls: "bg-[#F5F0FF] text-[#7C3AED] border-[#E9D5FF]" },
  completed: { label: "Completato", cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" },
  cancelled: { label: "Annullato",  cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]" },
};

// "I miei appuntamenti": tutte le prenotazioni della cliente, con la disdetta
// online di quelle future (entro il preavviso deciso dal centro).
export default function AppointmentsPage() {
  const { slug, api, info } = useTenant();
  const tz = info?.timezone || "Europe/Rome";
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(null); // id in disdetta

  const load = useCallback(() => {
    if (!api) return;
    api.myAppointments().then(setItems).catch((e) => setError(fmtError(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (a) => {
    if (busy) return;
    if (!window.confirm(`Disdire ${a.service_name} del ${fmtDate(a.starts_at, tz)}?`)) return;
    setBusy(a.id);
    setError("");
    setMsg("");
    try {
      await api.cancelAppt(a.id);
      setMsg("Appuntamento disdetto: il posto torna libero per altre clienti.");
      await load();
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!items) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
  }

  const now = new Date();
  const upcoming = items.filter((a) => a.status === "booked" && new Date(a.starts_at) >= now)
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const past = items.filter((a) => !upcoming.includes(a))
    .sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));

  const Row = ({ a, canCancel }) => {
    const meta = STATUS_META[a.status] || STATUS_META.booked;
    return (
      <li data-testid={`my-appt-${a.id}`} className="px-6 md:px-8 py-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-medium text-[#1A1A1A]">{a.service_name || "Trattamento"}</div>
          <div className="text-xs text-[#737373]">
            {fmtDate(a.starts_at, tz)}{a.duration_minutes ? ` · ${a.duration_minutes} min` : ""}
          </div>
          {a.staff_name && (
            <div className="mt-1 text-xs text-[#4A4A4A] inline-flex items-center gap-1"><User size={11} /> {a.staff_name}</div>
          )}
        </div>
        <div className="text-right">
          {a.price != null && <div className="text-sm font-medium text-[#1A1A1A]">{fmtEUR(a.price)}</div>}
          <span className={`mt-1 inline-block text-[10px] tracking-[0.2em] uppercase border rounded-full px-2 py-0.5 ${meta.cls}`}>
            {meta.label}
          </span>
        </div>
        {canCancel && (
          <button
            data-testid={`my-appt-cancel-${a.id}`}
            onClick={() => cancel(a)}
            disabled={!!busy}
            className="inline-flex items-center gap-1 text-xs bg-white border border-black/10 text-[#1A1A1A] rounded-full px-3 py-1.5 hover:bg-[#FAFAFA] disabled:opacity-50"
          >
            {busy === a.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Disdici
          </button>
        )}
      </li>
    );
  };

  return (
    <div data-testid="my-appointments-page" className="max-w-4xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Appuntamenti</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            I tuoi <span className="italic">appuntamenti.</span>
          </h1>
        </div>
        <Link to={`/${slug}/app/book`} data-testid="my-appt-book"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333]">
          <CalendarPlus size={14} /> Nuova prenotazione
        </Link>
      </div>

      {error && <div data-testid="my-appt-error" className="mt-6 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">{error}</div>}
      {msg && <div data-testid="my-appt-msg" className="mt-6 text-sm text-[#15803D]">{msg}</div>}

      <h2 className="mt-10 mb-4 font-serif text-2xl text-[#1A1A1A]">In programma</h2>
      <div className="rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="my-appt-upcoming">
        {upcoming.length === 0 ? (
          <div className="p-10 text-center text-[#737373] font-light">Nessun appuntamento in programma.</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {upcoming.map((a) => <Row key={a.id} a={a} canCancel />)}
          </ul>
        )}
      </div>
      <p className="mt-3 text-xs text-[#737373] font-light">
        Se non riesci a disdire online (per esempio a ridosso dell'appuntamento), contatta il centro.
      </p>

      {past.length > 0 && (
        <>
          <h2 className="mt-12 mb-4 font-serif text-2xl text-[#1A1A1A]">Passati e annullati</h2>
          <div className="rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="my-appt-past">
            <ul className="divide-y divide-black/5">
              {past.map((a) => <Row key={a.id} a={a} canCancel={false} />)}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
